import { memo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import {
  Brain,
  Filter,
  GitBranch,
  Inbox,
  Mail,
  Plus,
  Shield,
  Star,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FlowAiNodeData, FlowFeedNodeData, FlowRuleNodeData } from '@/lib/routing-flow-graph'

type RuleNodeProps = NodeProps & { data: FlowRuleNodeData }
type FeedNodeProps = NodeProps & { data: FlowFeedNodeData }
type AiNodeProps = NodeProps & { data: FlowAiNodeData }

function NodeShell({
  children,
  className,
  selected,
  disabled,
}: {
  children: React.ReactNode
  className?: string
  selected?: boolean | undefined
  disabled?: boolean | undefined
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-white shadow-sm transition-shadow',
        selected && 'ring-2 ring-indigo-500 ring-offset-2',
        disabled && 'opacity-50',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const FeedFlowNode = memo(function FeedFlowNode({ data }: FeedNodeProps) {
  return (
    <NodeShell className="min-w-[200px] border-amber-200/80 bg-gradient-to-b from-amber-50 to-white">
      <Handle type="source" position={Position.Bottom} className="!bg-amber-500 !w-2 !h-2" />
      <div className="flex items-start gap-2.5 px-3.5 py-3">
        <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
          <Mail className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-ovation-ink">{data.label}</span>
            {data.isDefault && <Star className="h-3 w-3 shrink-0 text-amber-500" />}
          </div>
          <div className="truncate text-[11px] text-ovation-muted">{data.address}</div>
        </div>
      </div>
    </NodeShell>
  )
})

export const AiIngestorFlowNode = memo(function AiIngestorFlowNode({ data }: AiNodeProps) {
  return (
    <NodeShell className="min-w-[280px] border-indigo-200 bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-700 text-white shadow-md shadow-indigo-200/50">
      <Handle type="target" position={Position.Top} className="!bg-white !w-2 !h-2" />
      <Handle type="source" position={Position.Bottom} className="!bg-white !w-2 !h-2" />
      <div className="flex items-center justify-center gap-3 px-6 py-3.5">
        <Brain className="h-5 w-5 shrink-0 opacity-90" />
        <div>
          <div className="text-sm font-semibold tracking-wide">{data.label}</div>
          <div className="text-[11px] text-indigo-100">
            Classify · summarise · score urgency before routing
          </div>
        </div>
      </div>
    </NodeShell>
  )
})

function RuleFlowNodeInner({
  data,
  selected,
  variant,
}: {
  data: FlowRuleNodeData
  selected?: boolean
  variant: 'guard' | 'catchAll' | 'branch' | 'subFilter'
}) {
  const Icon =
    variant === 'guard' ? Shield : variant === 'catchAll' ? Inbox : variant === 'branch' ? GitBranch : Filter

  return (
    <NodeShell
      selected={selected}
      disabled={!data.enabled}
      className={cn(
        'min-w-[220px] max-w-[260px] border-ovation-ink/10',
        variant === 'catchAll' && 'border-dashed border-slate-300 bg-slate-50',
        variant === 'branch' && cn('border-l-4', data.accentClass),
        variant === 'subFilter' && 'border-l-2 border-l-indigo-300 bg-indigo-50/30',
        variant === 'guard' && 'border-rose-200 bg-rose-50/50',
      )}
    >
      <Handle type="target" position={Position.Top} className="!bg-indigo-400 !w-2 !h-2" />
      {variant !== 'catchAll' && (
        <Handle type="source" position={Position.Bottom} className="!bg-indigo-400 !w-2 !h-2" />
      )}

      <div className="px-3.5 py-3">
        <div className="flex items-start gap-2">
          <div
            className={cn(
              'rounded-md p-1.5',
              variant === 'catchAll' ? 'bg-slate-200 text-slate-600' : 'bg-indigo-50 text-indigo-600',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-ovation-ink">{data.label}</div>
            <div className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-ovation-muted">
              {data.subtitle}
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1.5 border-t border-ovation-ink/5 pt-2 text-[10px]">
          <Users className="h-3 w-3 shrink-0 text-ovation-muted" />
          <span className="truncate font-medium text-ovation-ink/80">{data.destination}</span>
        </div>
        {variant === 'catchAll' && (
          <div className="mt-1 text-[9px] uppercase tracking-wider text-slate-500">
            Default destination
          </div>
        )}
      </div>
    </NodeShell>
  )
}

export const GuardFlowNode = memo(function GuardFlowNode(props: RuleNodeProps) {
  return <RuleFlowNodeInner data={props.data} selected={props.selected} variant="guard" />
})

export const CatchAllFlowNode = memo(function CatchAllFlowNode(props: RuleNodeProps) {
  return <RuleFlowNodeInner data={props.data} selected={props.selected} variant="catchAll" />
})

export const BranchFlowNode = memo(function BranchFlowNode(props: RuleNodeProps) {
  return <RuleFlowNodeInner data={props.data} selected={props.selected} variant="branch" />
})

export const SubFilterFlowNode = memo(function SubFilterFlowNode(props: RuleNodeProps) {
  return <RuleFlowNodeInner data={props.data} selected={props.selected} variant="subFilter" />
})

export const AddBranchHintNode = memo(function AddBranchHintNode() {
  return (
    <div className="flex h-[88px] w-[220px] items-center justify-center rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 text-indigo-600">
      <Plus className="mr-1.5 h-4 w-4" />
      <span className="text-xs font-medium">Add branch</span>
    </div>
  )
})

export const flowNodeTypes = {
  feed: FeedFlowNode,
  aiIngestor: AiIngestorFlowNode,
  guard: GuardFlowNode,
  catchAll: CatchAllFlowNode,
  branch: BranchFlowNode,
  subFilter: SubFilterFlowNode,
}
